import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import toast from "react-hot-toast";
import { useCreateService } from "../../hooks/services/useCreateService";
import { useUpdateService } from "../../hooks/services/useUpdateService";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";

const FormAddEditService = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Form States
    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [slug, setSlug] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [price, setPrice] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [images, setImages] = useState([]);

    // Hooks
    const { createServiceMutate, isPending: isCreating } = useCreateService();
    const { updateServiceMutate, isPending: isUpdating } = useUpdateService();

    const loadingSubmit = isCreating || isUpdating;

    // Auto-slug generation from English name (only in English mode and slug is empty)
    useEffect(() => {
        if (activeLang === "en" && nameEn && (!isEdit || !slug)) {
            setSlug(slugify(nameEn));
        }
    }, [nameEn, activeLang, isEdit, slug]);

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || currentData?.name || "");
            setNameEn(currentData?.name?.en || currentData?.name || "");
            setSlug(currentData?.slug || "");
            setDescriptionAr(currentData?.description?.ar || currentData?.description || "");
            setDescriptionEn(currentData?.description?.en || currentData?.description || "");
            setPrice(currentData?.price || "");
            setIsActive(currentData?.is_active ?? true);
            setImages(currentData?.images || []);
        }
    }, [isEdit, currentData]);

    const handleSlugChange = (e) => {
        const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
        setSlug(val);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!slug) {
            toast.error(isArabic ? "يرجى إدخال الرابط (Slug)" : "Please enter a slug");
            return;
        }

        const payload = {
            name: { ar: nameAr || nameEn, en: nameEn || nameAr },
            slug,
            description: { ar: descriptionAr || descriptionEn, en: descriptionEn || descriptionAr },
            price,
            is_active: isActive,
        };

        // إضافة الصور إن وجدت صور جديدة
        const hasNewImages = images.some(img => img.file);
        if (hasNewImages) {
            const formData = new FormData();
            formData.append("name[ar]", payload.name.ar);
            formData.append("name[en]", payload.name.en);
            formData.append("slug", slug);
            formData.append("description[ar]", payload.description.ar);
            formData.append("description[en]", payload.description.en);
            formData.append("price", price);
            formData.append("is_active", isActive ? "1" : "0");
            images.forEach((image, index) => {
                if (image.file) {
                    formData.append(`images[${index}]`, image.file);
                }
            });

            if (isEdit) {
                updateServiceMutate({ id: currentData.id, data: formData });
            } else {
                createServiceMutate(formData);
            }
        } else {
            if (isEdit) {
                updateServiceMutate({ id: currentData.id, data: payload });
            } else {
                createServiceMutate(payload);
            }
        }

        if (onSubmitSuccess) onSubmitSuccess();
    };

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل الخدمة" : "Edit Service") : (isArabic ? "إضافة خدمة جديدة" : "Add New Service")}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(l) => setActiveLang(l)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Multilingual Names */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="اسم الخدمة (عربي)"
                                placeholder="أدخل اسم الخدمة بالعربي"
                                type="text"
                                value={nameAr}
                                onChange={(e) => setNameAr(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Service Name (English)"
                                placeholder="Enter service name in English"
                                type="text"
                                value={nameEn}
                                onChange={(e) => setNameEn(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {/* Slug */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug - إنجليزي فقط)" : "Slug (English only)"}
                            placeholder="service-name"
                            type="text"
                            value={slug}
                            onChange={handleSlugChange}
                            disabled={activeLang === "en"}
                            required
                            helperText={activeLang === "ar" ? (isArabic ? "يجب إدخال الرابط باللغة الإنجليزية" : "Slug must be entered in English") : ""}
                        />
                    </Grid>

                    {/* Price */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "السعر" : "Price"}
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            required
                            fullWidth
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>

                    {/* Multilingual Descriptions */}
                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <FormInput
                                label="وصف الخدمة (عربي)"
                                placeholder="أدخل وصف الخدمة بالعربي"
                                type="textarea"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                minRows={3}
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <FormInput
                                label="Service Description (English)"
                                placeholder="Enter service description in English"
                                type="textarea"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                minRows={3}
                            />
                        </Grid>
                    )}

                    {/* Is Active Switch */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "نشط" : "Active"}
                            type="switch"
                            name="is_active"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                    </Grid>

                    {/* Service Images */}
                    <Grid item xs={12}>
                        <FormInput
                            label={isArabic ? "صور الخدمة" : "Service Images"}
                            type="multi-image"
                            name="images"
                            value={images}
                            onChange={setImages}
                            maxImages={10}
                        />
                    </Grid>
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "حفظ" : "Save"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditService;